/**
 * Create flow constants
 */

export const CREATE_ROUTES = {
  /** Nested under Fixtures stack so Back pops without remounting scoreboard */
  LEAGUE_DETAIL: '/app/gameResults/league-detail',
} as const

export const MAX_GAMES_PER_CHALLENGE = 5
export const MAX_GAMES_PER_CHALLENGE_PRO = 15

/**
 * Open Challenges (public board). A reserved opponent id that rides the existing `selectedFriendId`
 * plumbing (exactly like the Monkey) so "Post to anyone" needs no new create-context state. The
 * submit hook detects it and posts an open challenge (isOpen) with no named opponent.
 * See specs/open-challenges.
 */
export const OPEN_CHALLENGE_PARTICIPANT_ID = '__open__'

/** Preset stake options for the create challenge flow. */
export const STAKE_PRESETS = [
  { value: 0, label: '0 Coins', sublabel: 'Just for Fun' },
  { value: 50, label: '50 Coins' },
  { value: 100, label: '100 Coins' },
] as const;

/** Increment/decrement amount for stake +/- stepper. */
export const STAKE_STEP = 1;

/**
 * Rival-first review-screen stake stepper (specs/challenge-flow-redesign).
 * Step of 50 with a floor of 0 so free "Just for Fun" challenges stay possible.
 */
export const REVIEW_STAKE_STEP = 50;
export const REVIEW_STAKE_MIN = 0;

/** Test IDs for the rival-first create flow (used by Storybook play assertions). */
export const CREATE_FLOW_TEST_IDS = {
  rivalStep: 'create-rival-step',
  rivalTabFriends: 'create-rival-tab-friends',
  rivalTabGroups: 'create-rival-tab-groups',
  buildStep: 'create-build-step',
  buildToggleDeck: 'create-build-toggle-deck',
  buildToggleList: 'create-build-toggle-list',
  buildReviewButton: 'create-build-review-button',
  buildScore: 'create-build-score',
  vsHeader: 'create-vs-header',
  deck: 'create-build-deck',
  deckNext: 'create-build-deck-next',
  deckPrev: 'create-build-deck-prev',
  deckEnd: 'create-build-deck-end',
  deckProgress: 'create-build-deck-progress',
  list: 'create-build-list',
  reviewStep: 'create-review-step',
  reviewSend: 'create-review-send',
  stakeStepper: 'create-review-stake-stepper',
  stakeMinus: 'create-review-stake-minus',
  stakePlus: 'create-review-stake-plus',
  stakeValue: 'create-review-stake-value',
} as const;

/** Max future days for create challenge (aligns with fixture sync window). Dates beyond this are shown as locked; no API calls for them. */
export const CREATE_FIXTURE_WINDOW_DAYS = 8

/** Sub-label under create calendar explaining when odds open. */
export const CREATE_CALENDAR_HINT =
  'Match odds typically open 8 days before kickoff.'
/** Initial load: 10 days (today through today+9). */
export const INITIAL_DAYS_COUNT = 10
/** When within this many days of cached end, fetch 10 more. */
export const CACHE_EXTEND_THRESHOLD = 3
/** How many days to add when extending cache. */
export const CACHE_EXTEND_BATCH = 10
/** When picking uncached date: days before to fetch. */
export const PICK_BUFFER_BEFORE = 3
/** When picking uncached date: days after to fetch. */
export const PICK_BUFFER_AFTER = 8

/**
 * League names (lowercase) in display order when API displayOrder is null.
 * Lower index = higher in list.
 * Leagues not in this list are assigned a sentinel priority and sort last (then by name).
 */
export const LEAGUE_DISPLAY_PRIORITY: string[] = [
  // --- Tier 0: World Cup — always first while the tournament runs ---
  // The server relabels the World Cup league → "Football 2026" at the API boundary
  // (server/src/constants/leagues.constants.js LEAGUE_NAME_OVERRIDES — trademark-safe),
  // so THAT is the name this list must match, not "world cup". Bare 'football' covers the
  // pre-override serialization (generic scrubber output) so ordering never regresses.
  'football 2026',
  'football',

  // --- Tier 1: The Big Five & Champions League ---
  'premier league',
  'la liga',
  'serie a',
  'bundesliga',
  'ligue 1',
  'uefa champions league',

  // --- Tier 2: Major European Competitions & Israel ---
  'uefa europa league',
  'uefa europa conference league',
  'ligat ha\'al', // Added based on local priority
  'english league championship',

  // --- Tier 3: Strong European National Leagues ---
  'eredivisie',
  'primeira liga',
  'scottish premiership',
  'belgian first division a',
  'turkish super lig',
  'austrian bundesliga', // Added
  'swiss super league', // Added
  'greek super league', // Added

  // --- Tier 4: Americas & Global Popularity ---
  'mls',
  'argentina liga profesional',
  'brazilian serie a',
  'mexico liga mx', // Added
  'copa libertadores', // Added (Major South American tournament)

  // --- Tier 5: Secondary English & Emerging Leagues ---
  'english league one', // Added
  'saudi pro league', // Added (High interest due to player transfers)
  'j1 league', // Added (Japan)
  'k league 1', // Added (South Korea)
];
